// src/modules/CommandesModule.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { intendedRequest, releaseRejectedRequest } from '../utils/intendedRequest';
import { motion } from 'framer-motion';
import { useBusinessContext } from '../hooks/useBusinessContext';
import { getProducts, getCustomers, createSale, getSales, createInvoiceFromSale, getInvoice, addSalePayment } from '../services/apiClient';
import { generateInvoicePDF } from './utils/PdfGenerator';
import OrdersPanel from './shop/OrdersPanel';
import PaymentModal from '../components/ui/PaymentModal';
import LoadingInline from '../components/ui/LoadingInline';
import { tokens } from '../styles/tokens';

const animations = {
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 }
  },
  staggerContainer: {
    animate: { transition: { staggerChildren: 0.05 } },
  },
  scaleOnHover: {
    whileHover: { scale: 1.01, y: -2 },
    transition: { duration: 0.2 }
  }
};

// =====================================================
// ICÔNES
// =====================================================
const Icons = {
  package: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>,
  check: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  trash: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  clock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  truck: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  x: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  filter: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  globe: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  dollar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14.5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  mailbox: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 7h14v12H5z"/><path d="M2 7h3M19 7h3"/><path d="M8 11h8"/></svg>,
  store: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10h18"/><path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9"/><path d="m4 10 1.2-5A1 1 0 0 1 6.17 4h11.66a1 1 0 0 1 .98.8L20 10"/><path d="M9 14h6"/></svg>,
};

// =====================================================
// COMPOSANTS RÉUTILISABLES
// =====================================================

const Card = ({ children, style = {}, onClick, hover = true }) => (
  <motion.div
    {...(hover && animations.scaleOnHover)}
    style={{
      background: tokens.colors.surface,
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.colors.border}`,
      boxShadow: tokens.shadows.sm,
      overflow: 'hidden',
      ...style,
    }}
    onClick={onClick}
  >
    {children}
  </motion.div>
);

const Badge = ({ children, variant = 'default', style = {} }) => {
  const variants = {
    default: { bg: tokens.colors.primary + '15', color: tokens.colors.primary },
    success: { bg: tokens.colors.success + '15', color: tokens.colors.success },
    warning: { bg: tokens.colors.warning + '15', color: tokens.colors.warning },
    danger: { bg: tokens.colors.danger + '15', color: tokens.colors.danger },
    info: { bg: tokens.colors.info + '15', color: tokens.colors.info },
  };
  const v = variants[variant] || variants.default;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 12px',
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      background: v.bg,
      color: v.color,
      ...style,
    }}>
      {children}
    </span>
  );
};

const Button = ({ children, variant = 'primary', size = 'md', onClick, icon = null, style = {}, disabled = false }) => {
  const variants = {
    primary: { bg: tokens.colors.primary, color: 'white', border: 'none' },
    secondary: { bg: tokens.colors.secondary, color: 'white', border: 'none' },
    success: { bg: tokens.colors.success, color: 'white', border: 'none' },
    outline: { bg: 'transparent', color: tokens.colors.text, border: `1px solid ${tokens.colors.border}` },
    ghost: { bg: 'transparent', color: tokens.colors.textMuted, border: 'none' },
    danger: { bg: tokens.colors.danger + '10', color: tokens.colors.danger, border: `1px solid ${tokens.colors.danger}30` },
  };
  const v = variants[variant] || variants.primary;
  const sizes = {
    sm: { padding: '8px 12px', fontSize: 13 },
    md: { padding: '12px 20px', fontSize: 14 },
    lg: { padding: '16px 28px', fontSize: 16 },
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      disabled={disabled}
      style={{
        borderRadius: tokens.radius.md,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacing.sm,
        background: v.bg,
        color: v.color,
        border: v.border,
        ...sizes[size],
        ...style,
      }}
    >
      {icon}
      {children}
    </motion.button>
  );
};

// =====================================================
// HOOKS SUPABASE
// =====================================================

// =====================================================
// VENTES — nouveau système de vente (Business -> Shop -> Product -> Stock ->
// Customer -> Sale -> Invoice). Décision de cette mission (étape 21): ce
// module héberge désormais DEUX sections clairement séparées par onglets:
//   - "Ventes" (ci-dessous): le nouveau système, source de vérité serveur,
//     stock décrémenté automatiquement à chaque vente.
//   - "Commandes en ligne" (code existant plus bas, inchangé): les commandes
//     historiques passées depuis la vitrine publique d'une boutique.
// Les deux ne sont pas fusionnées pour ne pas créer un concept ambigu tant
// que la vitrine publique n'est pas elle-même reconnectée à /api/sales
// (prévu dans une prochaine mission).
// =====================================================

function VentesPanel() {
  const { businessId, business, shops, activeShopId, setActiveShopId, loading: businessLoading, error: businessError } = useBusinessContext();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [cart, setCart] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customerId, setCustomerId] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  // Mission 02: montant reçu distinct du total - vide = "payé en totalité".
  // Le serveur ne fait jamais confiance à un payment_status envoyé par le
  // navigateur: il est toujours recalculé côté backend (voir create_sale).
  const [amountReceivedInput, setAmountReceivedInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const saleRequest = useRef(null);
  const saleInFlight = useRef(false);
  const cartScope = useRef(null);
  const loadSequence = useRef(0);
  const [catalogScope, setCatalogScope] = useState('');
  const scope = `${businessId}:${activeShopId}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [saleError, setSaleError] = useState('');
  const [lastSale, setLastSale] = useState(null);
  const [invoicingSaleId, setInvoicingSaleId] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const [invoiceNotice, setInvoiceNotice] = useState('');
  const [payingSale, setPayingSale] = useState(null);

  const load = useCallback(async () => {
    if (!businessId || !activeShopId || currentScope.current !== `${businessId}:${activeShopId}`) return;
    const sequence = ++loadSequence.current;
    setLoading(true);
    setLoadError('');
    try {
      const [productsRes, customersRes, salesRes] = await Promise.all([
        getProducts({ businessId, shopId: activeShopId }),
        getCustomers({ businessId, limit: 200 }),
        getSales({ businessId, shopId: activeShopId || undefined, limit: 20 })
      ]);
      if (sequence !== loadSequence.current) return;
      setCatalogScope(`${businessId}:${activeShopId}`);
      setProducts(productsRes.products || []);
      setCustomers(customersRes.customers || []);
      setSales(salesRes.sales || []);
    } catch (err) {
      if (sequence === loadSequence.current) setLoadError(err?.message || 'Impossible de charger les données de vente.');
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [businessId, activeShopId]);

  useEffect(() => { load(); return () => { loadSequence.current += 1; }; }, [load]);

  useEffect(() => {
    if (!cartScope.current || cartScope.current === scope) return;
    if (!businessId || !activeShopId) {
      setSaleError('Impossible de déterminer votre entreprise ou votre boutique. Rechargez la page et réessayez.');
      return;
    }
    if (saleRequest.current || saleInFlight.current) {
      setSaleError('Une vente reste à vérifier. Revenez à la boutique de cette vente pour réessayer.');
      return;
    }
    setCart([]);
    cartScope.current = null;
    setSelectedProductId('');
    setQuantity(1);
    setDiscountAmount(0);
    setAmountReceivedInput('');
    setSaleError('La boutique a changé. Le panier a été vidé pour éviter une vente dans la mauvaise boutique.');
  }, [scope, businessId, activeShopId]);

  const visibleProducts = catalogScope === scope
    ? products.filter((product) => product.shop_id == null || product.shop_id === activeShopId)
    : [];

  const changeShop = (shopId) => {
    if (saleRequest.current || saleInFlight.current) {
      setSaleError('Une vente reste à vérifier. Réessayez avant de changer de boutique.');
      return;
    }
    setActiveShopId(shopId);
  };

  const addToCart = () => {
    const product = visibleProducts.find((p) => p.id === selectedProductId);
    if (!product) return;
    if (cartScope.current && cartScope.current !== scope) return;
    cartScope.current = scope;
    const qty = Math.max(1, Number(quantity) || 1);
    setCart((prev) => {
      const existing = prev.find((line) => line.productId === product.id);
      if (existing) {
        return prev.map((line) => (line.productId === product.id ? { ...line, quantity: line.quantity + qty } : line));
      }
      return [...prev, { productId: product.id, shopId: product.shop_id, name: product.name, quantity: qty, unitPrice: Number(product.selling_price) }];
    });
    setSelectedProductId('');
    setQuantity(1);
  };

  const removeFromCart = (productId) => setCart((prev) => prev.filter((l) => l.productId !== productId));

  const cartTotal = cart.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0);
  const totalAfterDiscount = Math.max(0, cartTotal - (Number(discountAmount) || 0));
  const amountReceived = amountReceivedInput === '' ? totalAfterDiscount : Math.max(0, Number(amountReceivedInput) || 0);
  const remaining = Math.max(0, totalAfterDiscount - amountReceived);
  const isCredit = remaining > 0;
  const creditBlockedByMissingCustomer = isCredit && !customerId;

  const submitSale = async () => {
    if (saleInFlight.current) return;
    if (cart.length === 0 || creditBlockedByMissingCustomer) return;
    // Mission hotfix P0 / bug 4: the guard below already blocked a missing
    // shop but never checked businessId itself - a merchant could reach this
    // button while BusinessContext was still resolving (or had failed
    // silently) and the raw backend validation error ("businessId: Required")
    // would surface instead of a clean, actionable message.
    if (!businessId || !activeShopId) {
      setSaleError('Impossible de déterminer votre entreprise ou votre boutique. Rechargez la page et réessayez.');
      return;
    }
    if (cartScope.current !== scope || cart.some((line) => line.shopId != null && line.shopId !== activeShopId)) {
      setSaleError('Le panier appartient à une autre boutique. Revenez à cette boutique ou recommencez la vente.');
      return;
    }
    saleInFlight.current = true;
    setSubmitting(true);
    setSaleError('');
    setLastSale(null);
    setInvoiceError('');
    setInvoiceNotice('');
    try {
      const { sale } = await createSale(intendedRequest(saleRequest, {
        businessId,
        shopId: activeShopId,
        customerId: customerId || undefined,
        items: cart.map((l) => ({ productId: l.productId, quantity: l.quantity })),
        discountAmount: Number(discountAmount) || 0,
        paymentMethod,
        amountPaid: amountReceived
      }));
      saleRequest.current = null;
      setLastSale(sale);
      setCart([]);
      cartScope.current = null;
      setCustomerId('');
      setDiscountAmount(0);
      setAmountReceivedInput('');
      await load();
    } catch (err) {
      releaseRejectedRequest(saleRequest, err);
      if (err?.code === 'INSUFFICIENT_STOCK') {
        setSaleError('Stock insuffisant pour un des produits sélectionnés.');
      } else if (err?.code === 'PRODUCT_NOT_FOUND') {
        setSaleError('Un produit du panier est introuvable pour cette boutique.');
      } else if (err?.code === 'CREDIT_REQUIRES_CUSTOMER') {
        setSaleError('Sélectionnez un client pour enregistrer une vente non payée en totalité.');
      } else if (err?.code === 'OVERPAYMENT') {
        setSaleError('Le montant reçu dépasse le total de la vente.');
      } else if (err?.status === 401) {
        setSaleError('Session expirée: reconnectez-vous.');
      } else {
        setSaleError(err?.message || 'Vente impossible.');
      }
    } finally {
      saleInFlight.current = false;
      setSubmitting(false);
    }
  };

  // Mission M1: le serveur dérive l'entreprise de la vente elle-même;
  // businessId n'est envoyé que comme contrôle de cohérence. Une vente déjà
  // facturée renvoie sa facture existante (existing: true), qui est rouverte.
  const handleGenerateInvoice = async (sale) => {
    setInvoicingSaleId(sale.id);
    setInvoiceError('');
    setInvoiceNotice('');
    try {
      const { invoice, existing } = await createInvoiceFromSale(sale.id, businessId ? { businessId } : {});
      const detail = await getInvoice(invoice.id);
      const customer = customers.find((c) => c.id === sale.customer_id);
      generateInvoicePDF(
        {
          numero: detail.invoice.invoice_number,
          date: detail.invoice.issue_date,
          client: { nom: customer?.name || 'Client comptoir' },
          items: (detail.items || []).map((it) => ({
            description: it.description, quantite: it.quantity, prix: it.unit_price, tva: 0
          })),
          totals: { ht: detail.invoice.subtotal, tva: detail.invoice.tax_amount, ttc: detail.invoice.total }
        },
        { nom: business?.name || 'NSARA', email: business?.email || '', telephone: business?.phone || '' }
      );
      setInvoiceNotice(existing
        ? `Cette vente a déjà une facture (${detail.invoice.invoice_number}) : elle a été rouverte.`
        : `Facture ${detail.invoice.invoice_number} générée.`);
    } catch (err) {
      if (err?.code === 'INVOICE_ALREADY_EXISTS') {
        setInvoiceError('Une facture existe déjà pour cette vente.');
      } else if (err?.code === 'SALE_CANCELLED') {
        setInvoiceError('Cette vente est annulée : impossible de générer la facture.');
      } else if (err?.status === 401) {
        setInvoiceError('Session expirée : reconnectez-vous.');
      } else {
        setInvoiceError(err?.message || 'Impossible de générer la facture.');
      }
    } finally {
      setInvoicingSaleId('');
    }
  };

  if (businessLoading) return <LoadingInline text="Préparation de votre entreprise/boutique..." />;
  if (businessError) return <div style={{ padding: 20, color: tokens.colors.danger }}>{businessError}</div>;

  return (
    <div>
      {shops.length > 1 && (
        <div style={{ marginBottom: tokens.spacing.lg, display: 'flex', alignItems: 'center', gap: tokens.spacing.sm }}>
          {Icons.store}
          <select
            value={activeShopId}
            onChange={(e) => changeShop(e.target.value)}
            style={{ padding: '10px 16px', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, fontWeight: 600 }}
          >
            {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}

      <Card style={{ marginBottom: tokens.spacing.xl }}>
        <div style={{ padding: tokens.spacing.lg }}>
          <h3 style={{ marginTop: 0 }}>Nouvelle vente</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: tokens.spacing.sm, marginBottom: tokens.spacing.md }}>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              style={{ padding: 12, borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}
            >
              <option value="">Sélectionner un produit...</option>
              {visibleProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {Number(p.stock_quantity)} en stock — {Number(p.selling_price).toLocaleString()} F
                </option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{ padding: 12, borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}
            />
            <Button variant="outline" onClick={addToCart} disabled={!selectedProductId}>Ajouter</Button>
          </div>

          {cart.length > 0 && (
            <div style={{ background: tokens.colors.background, borderRadius: tokens.radius.md, padding: tokens.spacing.md, marginBottom: tokens.spacing.md }}>
              {cart.map((line) => (
                <div key={line.productId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                  <span>{line.name} × {line.quantity}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong>{(line.quantity * line.unitPrice).toLocaleString()} F</strong>
                    <button onClick={() => removeFromCart(line.productId)} style={{ background: 'none', border: 'none', color: tokens.colors.danger, cursor: 'pointer' }}>{Icons.x}</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: tokens.spacing.md, marginBottom: tokens.spacing.md }}>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} style={{ padding: 12, borderRadius: tokens.radius.md, border: `1px solid ${isCredit && !customerId ? tokens.colors.danger : tokens.colors.border}` }}>
              <option value="">Client comptoir (sans fiche)</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input
              type="number"
              min={0}
              placeholder="Remise (FCFA)"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e.target.value)}
              style={{ padding: 12, borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}
            />
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} style={{ padding: 12, borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}>
              <option value="CASH">Espèces</option>
              <option value="ORANGE_MONEY">Orange Money</option>
              <option value="WAVE">Wave</option>
              <option value="BANK_TRANSFER">Virement</option>
              <option value="OTHER">Autre</option>
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: tokens.spacing.md, marginBottom: tokens.spacing.md }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: tokens.colors.textMuted, marginBottom: 6 }}>Montant reçu</label>
              <input
                type="number"
                min={0}
                placeholder={`${totalAfterDiscount.toLocaleString()} (payé en totalité)`}
                value={amountReceivedInput}
                onChange={(e) => setAmountReceivedInput(e.target.value)}
                style={{ width: '100%', padding: 12, borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, boxSizing: 'border-box' }}
              />
            </div>
            {isCredit && (
              <div style={{
                background: `${tokens.colors.danger}12`, color: tokens.colors.danger, borderRadius: tokens.radius.md,
                padding: 12, display: 'flex', flexDirection: 'column', justifyContent: 'center'
              }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Reste à payer</span>
                <strong style={{ fontSize: 18 }}>{remaining.toLocaleString()} F</strong>
              </div>
            )}
          </div>

          {creditBlockedByMissingCustomer && (
            <div style={{ marginBottom: tokens.spacing.md, background: `${tokens.colors.danger}15`, color: tokens.colors.danger, padding: 12, borderRadius: tokens.radius.md, fontWeight: 600 }}>
              Sélectionnez un client ci-dessus pour enregistrer une vente non payée en totalité (crédit client).
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: tokens.colors.primary }}>
              Total: {totalAfterDiscount.toLocaleString()} F
            </div>
            <Button variant="success" icon={Icons.check} onClick={submitSale} disabled={submitting || cart.length === 0 || creditBlockedByMissingCustomer}>
              {submitting ? 'Enregistrement...' : 'Confirmer la vente'}
            </Button>
          </div>

          {saleError && (
            <div style={{ marginTop: tokens.spacing.md, background: `${tokens.colors.danger}15`, color: tokens.colors.danger, padding: 12, borderRadius: tokens.radius.md }}>
              {saleError}
            </div>
          )}

          {lastSale && (
            <div style={{ marginTop: tokens.spacing.md, background: `${tokens.colors.success}15`, padding: 16, borderRadius: tokens.radius.md, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <strong>Vente {lastSale.sale_number} enregistrée</strong>
                <p style={{ margin: '4px 0 0', color: tokens.colors.textMuted }}>Total: {Number(lastSale.total).toLocaleString()} F</p>
                {lastSale.remaining > 0 && (
                  <p style={{ margin: '4px 0 0', color: tokens.colors.danger, fontWeight: 700 }}>
                    Reste à payer : {Number(lastSale.remaining).toLocaleString()} F
                  </p>
                )}
              </div>
              <Button variant="primary" size="sm" onClick={() => handleGenerateInvoice(lastSale)} disabled={invoicingSaleId === lastSale.id}>
                {invoicingSaleId === lastSale.id ? 'Génération...' : 'Générer la facture'}
              </Button>
            </div>
          )}

          {invoiceNotice && (
            <div role="status" style={{ marginTop: tokens.spacing.md, background: `${tokens.colors.success}15`, color: tokens.colors.success, padding: 12, borderRadius: tokens.radius.md }}>
              {invoiceNotice}
            </div>
          )}

          {invoiceError && (
            <div role="alert" style={{ marginTop: tokens.spacing.md, background: `${tokens.colors.danger}15`, color: tokens.colors.danger, padding: 12, borderRadius: tokens.radius.md }}>
              {invoiceError}
            </div>
          )}
        </div>
      </Card>

      <h3>Ventes récentes</h3>
      {loadError && <div style={{ color: tokens.colors.danger, marginBottom: tokens.spacing.md }}>{loadError}</div>}
      {loading ? (
        <div style={{ color: tokens.colors.textMuted }}>Chargement...</div>
      ) : sales.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: tokens.colors.textMuted, border: `2px dashed ${tokens.colors.border}`, borderRadius: tokens.radius.lg }}>
          Aucune vente pour le moment.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing.sm }}>
          {sales.map((sale) => {
            const remaining = Number(sale.total) - Number(sale.amount_paid ?? sale.total);
            return (
              <Card key={sale.id}>
                <div style={{ padding: tokens.spacing.md, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <strong>{sale.sale_number}</strong>
                    <span style={{ marginLeft: 10, color: tokens.colors.textMuted, fontSize: 13 }}>
                      {new Date(sale.created_at).toLocaleString('fr-FR')}
                    </span>
                    {sale.status === 'CANCELLED' && <Badge variant="danger" style={{ marginLeft: 10 }}>Annulée</Badge>}
                    {sale.status !== 'CANCELLED' && sale.payment_status === 'PARTIAL' && (
                      <Badge variant="warning" style={{ marginLeft: 10 }}>Reste {remaining.toLocaleString()} F</Badge>
                    )}
                    {sale.status !== 'CANCELLED' && sale.payment_status === 'UNPAID' && (
                      <Badge variant="danger" style={{ marginLeft: 10 }}>Dette · {remaining.toLocaleString()} F</Badge>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <strong style={{ color: tokens.colors.primary }}>{Number(sale.total).toLocaleString()} F</strong>
                    {sale.status !== 'CANCELLED' && sale.payment_status !== 'PAID' && (
                      <Button variant="outline" size="sm" onClick={() => setPayingSale(sale)}>
                        Enregistrer un paiement
                      </Button>
                    )}
                    {sale.status !== 'CANCELLED' && (
                      <Button variant="outline" size="sm" onClick={() => handleGenerateInvoice(sale)} disabled={invoicingSaleId === sale.id}>
                        {invoicingSaleId === sale.id ? '...' : 'Facture'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {payingSale && (
        <PaymentModal
          sale={{
            saleNumber: payingSale.sale_number,
            customerName: customers.find((c) => c.id === payingSale.customer_id)?.name,
            remaining: Number(payingSale.total) - Number(payingSale.amount_paid ?? payingSale.total)
          }}
          onClose={() => { setPayingSale(null); load(); }}
          onSubmit={(payload) => addSalePayment(payingSale.id, payload)}
        />
      )}
    </div>
  );
}

// =====================================================
// COMPOSANT PRINCIPAL
// =====================================================

export default function CommandesModule() {
  const [activeTab, setActiveTab] = useState('ventes');
  const { businessId, shops, activeShopId, setActiveShopId, loading } = useBusinessContext();

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 100 }}>
      {/* ONGLETS: Ventes (comptoir) et Commandes en ligne (liens produits publics). */}
      <div style={{ display: 'flex', gap: tokens.spacing.sm, marginBottom: tokens.spacing.xl, borderBottom: `1px solid ${tokens.colors.border}` }}>
        {[{ id: 'ventes', label: 'Ventes' }, { id: 'commandes', label: 'Commandes en ligne' }].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderBottom: activeTab === tab.id ? `3px solid ${tokens.colors.primary}` : '3px solid transparent',
              background: 'none',
              color: activeTab === tab.id ? tokens.colors.primary : tokens.colors.textMuted,
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 15,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'ventes' && <VentesPanel />}

      {activeTab === 'commandes' && (
        loading ? (
          <p style={{ color: tokens.colors.textMuted }}>Chargement…</p>
        ) : (
          <>
            <div style={{ marginBottom: tokens.spacing.lg }}>
              <h2 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: tokens.colors.text }}>Commandes en ligne</h2>
              <p style={{ color: tokens.colors.textMuted, margin: '8px 0 0', fontSize: 14 }}>
                Commandes reçues via les liens produits partagés.
              </p>
            </div>

            {shops.length > 1 && (
              <div style={{ marginBottom: tokens.spacing.lg }}>
                <select
                  value={activeShopId}
                  onChange={(e) => setActiveShopId(e.target.value)}
                  style={{ padding: '10px 16px', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, fontWeight: 600 }}
                >
                  {shops.map((shop) => <option key={shop.id} value={shop.id}>{shop.name}</option>)}
                </select>
              </div>
            )}

            <OrdersPanel
              businessId={businessId}
              shopId={activeShopId}
              onViewSale={() => setActiveTab('ventes')}
            />
          </>
        )
      )}
    </div>
  );
}
